/**
 * OrderGroove PDP fallback:
 * Keep frequency row visibility in sync across browsers that are inconsistent with :has().
 */
(() => {
  const OFFER_SELECTOR = 'og-offer[location="pdp"]';
  const initializedOffers = new WeakSet();

  function isOptionSelected(option) {
    const radio = option.querySelector('input[type="radio"]');
    if (radio) return radio.checked;

    if (option.matches('[aria-selected="true"], [data-selected="true"], .is-selected, .selected')) {
      return true;
    }

    return !!option.querySelector('og-optin-button[aria-pressed="true"], og-optout-button[aria-pressed="true"]');
  }

  function setSelectedOption(offer, selectedOption) {
    const options = offer.querySelectorAll('.og-purchase-options .og-option');
    options.forEach((option) => {
      option.classList.toggle('og-option--selected', option === selectedOption);
    });
  }

  function syncOfferState(offer) {
    const options = Array.from(offer.querySelectorAll('.og-purchase-options .og-option'));
    if (!options.length) return;

    const selectedBySignal = options.find(isOptionSelected);

    if (selectedBySignal) {
      setSelectedOption(offer, selectedBySignal);
    }
  }

  function scheduleSync(offer) {
    if (!offer || offer._ogSyncScheduled) return;

    offer._ogSyncScheduled = true;

    requestAnimationFrame(() => {
      offer._ogSyncScheduled = false;
      syncOfferState(offer);
    });
  }

  function initOffer(offer) {
    if (initializedOffers.has(offer)) {
      scheduleSync(offer);
      return;
    }

    initializedOffers.add(offer);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          scheduleSync(offer);
          return;
        }

        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-pressed') {
          scheduleSync(offer);
          return;
        }
      }
    });

    observer.observe(offer, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-pressed']
    });

    offer.addEventListener('click', (event) => {
      const option = event.target.closest('.og-purchase-options .og-option');

      if (option && offer.contains(option)) {
        setSelectedOption(offer, option);
      }

      scheduleSync(offer);
    }, true);
    offer.addEventListener('change', () => scheduleSync(offer), true);

    scheduleSync(offer);
  }

  function initExistingOffers() {
    document.querySelectorAll(OFFER_SELECTOR).forEach(initOffer);
  }

  const rootObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;

        if (node.matches?.(OFFER_SELECTOR)) {
          initOffer(node);
          continue;
        }

        node.querySelectorAll?.(OFFER_SELECTOR).forEach(initOffer);
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initExistingOffers, { once: true });
  } else {
    initExistingOffers();
  }

  rootObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
